/* eslint-disable react/jsx-no-target-blank */
import { observer } from 'mobx-react';
import React from 'react';
import { UFBNoticeClickEvent } from '@udemy/browse-event-tracking';
import { ClickEvent, Tracker } from '@udemy/event-tracking';
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { ModalLanguageSelector } from '@udemy/language-selector';
import { toBusinessUdemy } from '@udemy/organization';
import { Avatar, BlockList } from '@udemy/react-core-components';
import { Badge } from '@udemy/react-messaging-components';
import { ProBadge } from '@udemy/react-pro-content';
import { useUDData } from '@udemy/ud-data';
import { CartBadge, ProfileBadge, UnreadActivityNotificationsBadge, UnreadMessagesBadge, UnseenCreditsBadge } from '../../badges.react-component';
import { LanguageSelectorLocation } from '../../constants';
import { showUserActivityLink } from '../../external/header-experiment-features';
import { useHeaderExperimentFeatures } from '../../hooks/use-header-experiment-features';
import { useHeaderStore } from '../../hooks/use-header-store';
import headerStyles from "../desktop-header.module.css";
import { HeaderDropdown, HeaderButton, HeaderMenu } from '../header-dropdown.react-component';
import { LanguageSelectorMenuItem } from '../language-selector/language-selector-menu-item.react-component';
import menuStyles from "../list-menu.module.css";
import styles from "./user-profile-dropdown.module.css";
import { handleTouchCapableClick } from './utils';
export const UserProfileDropdown = observer(({
  className,
  useLangPrefixedUrls = false
}) => {
  var _me$organization;
  const headerStore = useHeaderStore();
  const {
    gettext,
    locale
  } = useI18n();
  const {
    Config,
    me
  } = useUDData();
  const headerExperimentFeatures = useHeaderExperimentFeatures();
  const showActivityLink = showUserActivityLink({
    headerExperimentFeatures,
    headerStore
  });
  const handleTryUFBClick = () => {
    if (headerStore.tryUFBPlacements) {
      var _headerStore$tryUFBPl;
      Tracker.publishEvent(new UFBNoticeClickEvent({
        locale,
        placement: (_headerStore$tryUFBPl = headerStore.tryUFBPlacements) === null || _headerStore$tryUFBPl === void 0 ? void 0 : _headerStore$tryUFBPl.profile
      }));
    }
  };
  const renderSubscriptionBlock = () => {
    const {
      urls,
      userSpecificContext
    } = headerStore;
    if (userSpecificContext.isSubscriptionAware) {
      return /*#__PURE__*/React.createElement(BlockList.Item, {
        color: "neutral",
        href: urls.SUBSCRIPTION_MANAGEMENT
      }, /*#__PURE__*/React.createElement("div", {
        className: styles['subscription-menu-item']
      }, gettext('Subscriptions'), userSpecificContext.user.consumer_subscription_active && headerStore.isGrowWithGoogleSubscriber === false && headerStore.isValuePlanSubscriber === false && /*#__PURE__*/React.createElement(Badge, {
        className: styles['badge-personal-plan']
      }, gettext('Personal Plan'))));
    }
    return null;
  };
  const renderUfbSection = () => {
    var _tryUFBPlacements$pro;
    const {
      tryUFBPlacements
    } = headerStore;
    const {
      productLink
    } = headerStore.userSpecificContext;
    const hasOrganization = Config.brand.has_organization;
    return !hasOrganization ? /*#__PURE__*/React.createElement("a", {
      className: styles['try-ufb-section'],
      href: toBusinessUdemy('request-demo', {
        ref: (_tryUFBPlacements$pro = tryUFBPlacements === null || tryUFBPlacements === void 0 ? void 0 : tryUFBPlacements.profile) !== null && _tryUFBPlacements$pro !== void 0 ? _tryUFBPlacements$pro : ''
      }),
      target: productLink.isOnsiteRequestDemo ? undefined : '_blank',
      rel: productLink.isOnsiteRequestDemo ? undefined : 'noopener noreferrer',
      onClick: handleTryUFBClick
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "ud-heading-md"
    }, 'Udemy Business'), /*#__PURE__*/React.createElement("div", {
      className: `ud-text-sm ${styles['try-ufb-subtitle']}`
    }, gettext('Bring learning to your company'))), /*#__PURE__*/React.createElement(Icon, {
      glyph: "open-in-new",
      size: "medium",
      label: false
    })) : null;
  };
  const {
    urls
  } = headerStore;
  const {
    user,
    isInstructor
  } = headerStore.userSpecificContext;
  const userIsPro = me.is_authenticated && ((_me$organization = me.organization) === null || _me$organization === void 0 ? void 0 : _me$organization.is_pro_license_holder);
  return /*#__PURE__*/React.createElement(HeaderDropdown, {
    trigger: /*#__PURE__*/React.createElement(HeaderButton, {
      componentClass: "a",
      href: urls.EDIT_PROFILE,
      udStyle: "image",
      "aria-label": gettext('My profile'),
      "data-purpose": "user-dropdown",
      onClick: handleTouchCapableClick
    }, /*#__PURE__*/React.createElement(Avatar, {
      user: user,
      alt: "NONE",
      size: "small",
      "aria-hidden": "true",
      className: styles['dropdown-button-avatar']
    }), /*#__PURE__*/React.createElement(ProfileBadge, {
      dot: true,
      className: headerStyles['dropdown-dot-badge']
    })),
    className: `${className} ${menuStyles['list-menu-container']}`
  }, /*#__PURE__*/React.createElement(HeaderMenu, null, /*#__PURE__*/React.createElement("a", {
    href: urls.EDIT_PROFILE,
    className: userIsPro ? styles['pro-user-section'] : styles['user-section']
  }, /*#__PURE__*/React.createElement(Avatar, {
    user: user,
    alt: "NONE",
    className: styles['user-section-avatar']
  }), /*#__PURE__*/React.createElement("div", {
    className: styles['user-details']
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "ud-heading-md"
  }, user.display_name), /*#__PURE__*/React.createElement("div", {
    className: `ud-text-xs ${styles['email']}`
  }, user.email), userIsPro && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(ProBadge, null))))), /*#__PURE__*/React.createElement(BlockList, {
    size: "small",
    className: menuStyles['section'],
    iconAlignment: "right"
  }, /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.MY_LEARNING
  }, gettext('My learning')), showActivityLink && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.MY_ACTIVITY,
    onClick: () => {
      Tracker.publishEvent(new ClickEvent({
        componentName: 'headerToMyActivity'
      }));
    }
  }, gettext('My activity')), Config.features.coaching && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.COACHING_BOOKINGS
  }, gettext('My 1-1 bookings')), Config.features.shopping_cart && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.CART,
    icon: /*#__PURE__*/React.createElement(CartBadge, null)
  }, gettext('My cart')), Config.features.wishlist && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.WISHLIST
  }, gettext('Wishlist')), Config.brand.is_teaching_enabled && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.TEACH
  }, isInstructor ? gettext('Instructor dashboard') : gettext('Teach on Udemy'))), /*#__PURE__*/React.createElement(BlockList, {
    size: "small",
    className: menuStyles['section'],
    iconAlignment: "right"
  }, Config.features.notifications && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.VIEW_NOTIFICATIONS,
    icon: /*#__PURE__*/React.createElement(UnreadActivityNotificationsBadge, null)
  }, gettext('Notifications')), Config.brand.is_messaging_enabled && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.MESSAGES,
    icon: /*#__PURE__*/React.createElement(UnreadMessagesBadge, null)
  }, gettext('Messages'))), /*#__PURE__*/React.createElement(BlockList, {
    size: "small",
    className: menuStyles['section'],
    iconAlignment: "right"
  }, /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.ACCOUNT
  }, gettext('Account settings')), !Config.brand.has_organization && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.PAYMENT_METHODS
  }, gettext('Payment methods')), renderSubscriptionBlock(), !Config.brand.has_organization && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.CREDITS,
    icon: /*#__PURE__*/React.createElement(UnseenCreditsBadge, null)
  }, gettext('Udemy credits')), !Config.brand.has_organization && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.PURCHASE_HISTORY
  }, gettext('Purchase history'))), /*#__PURE__*/React.createElement(BlockList, {
    size: "small",
    className: menuStyles['section'],
    iconAlignment: "right"
  }, /*#__PURE__*/React.createElement(ModalLanguageSelector, {
    uiRegion: LanguageSelectorLocation.DESKTOP_HEADER,
    useLangPrefixedUrls: useLangPrefixedUrls,
    trigger: /*#__PURE__*/React.createElement(LanguageSelectorMenuItem, {
      className: menuStyles.item
    })
  })), /*#__PURE__*/React.createElement(BlockList, {
    size: "small",
    className: menuStyles['section'],
    iconAlignment: "right"
  }, !!(Config.brand.is_profile_functions_enabled && Config.brand.is_user_profiles_public && user.url) && /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: user.url,
    target: "_blank",
    rel: "nofollow noopener noreferrer"
  }, gettext('Public profile')), /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.EDIT_PROFILE
  }, gettext('Edit profile'))), /*#__PURE__*/React.createElement(BlockList, {
    size: "small",
    className: menuStyles['section'],
    iconAlignment: "right"
  }, /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: urls.SUPPORT,
    target: "_blank",
    rel: "noopener noreferrer"
  }, gettext('Help and Support')), /*#__PURE__*/React.createElement(BlockList.Item, {
    color: "neutral",
    href: user.logout_url
  }, gettext('Log out'))), renderUfbSection()));
});
//# sourceMappingURL=user-profile-dropdown.react-component.js.map